<template>
  <div class="admin-page">
    <div class="admin-container">
      <div class="admin-header">
        <div class="admin-header__titles">
          <h1 class="admin-title"><i class="fa-solid fa-table-list"></i> All Services</h1>
          <p class="admin-subtitle">Browse, edit and audit every posting on the platform.</p>
        </div>
        <div class="admin-header__actions">
          <button class="admin-btn admin-btn--ghost" @click="$router.push('/')">
            <i class="fa-solid fa-house"></i> Go to Site
          </button>
        </div>
      </div>

      <div class="admin-card admin-section">
        <div class="admin-toolbar filters">
          <div class="admin-field email-field">
            <label class="admin-label">User Email or Service URL</label>
            <input
              class="admin-input"
              v-model="filterEmail"
              placeholder="email@example.com or https://renants.com/view-post/..."
              autocomplete="off"
              @input="onEmailInput"
              @focus="onEmailInput"
              @keydown.down.prevent="moveHighlight(1)"
              @keydown.up.prevent="moveHighlight(-1)"
              @keydown.enter.prevent="onEmailEnter"
              @keydown.esc="showSuggestions = false"
              @blur="hideSuggestionsSoon"
            />
            <ul v-if="showSuggestions && emailSuggestions.length" class="email-suggest">
              <li
                v-for="(email, i) in emailSuggestions"
                :key="email"
                :class="{ active: i === highlightIndex }"
                @mousedown.prevent="selectEmail(email)"
                @mouseenter="highlightIndex = i"
              >
                <i class="fa-solid fa-envelope"></i> {{ email }}
              </li>
            </ul>
          </div>
          <div class="admin-field">
            <label class="admin-label">From Date (Modified)</label>
            <DatePicker v-model="filterDateFrom" :max="filterDateTo" placeholder="Any start date" />
          </div>
          <div class="admin-field">
            <label class="admin-label">To Date (Modified)</label>
            <DatePicker v-model="filterDateTo" :min="filterDateFrom" placeholder="Any end date" />
          </div>
          <div class="admin-field">
            <label class="admin-label">Source</label>
            <SearchableSelect
              v-model="filterSource"
              :options="sourceOptions"
              :clearable="false"
              placeholder="Source"
              @change="onSourceChange"
            />
          </div>
          <div class="admin-field">
            <label class="admin-label">Sort By</label>
            <SearchableSelect
              v-model="sortBy"
              :options="sortOptions"
              :clearable="false"
              placeholder="Sort by"
              @change="currentPage = 1; fetchServices()"
            />
          </div>
          <button class="admin-btn admin-btn--primary" @click="currentPage = 1; fetchServices()" :disabled="loading">
            <i class="fa-solid fa-filter"></i> Apply Filter
          </button>
          <button class="admin-btn admin-btn--outline" @click="clearFilters">Clear</button>
        </div>
      </div>

      <div v-if="error" class="admin-badge admin-badge--danger admin-section">{{ error }}</div>
      <TableSkeleton v-if="loading" :columns="7" />

      <div v-if="items.length" class="admin-table-wrap admin-table-wrap--scroll">
        <!-- Seven columns instead of ten: type and location fold into the
             listing cell, the two dates share one cell under aligned
             sub-headers, and the dates are short-form with the full timestamp
             on hover. Widths are shares of the table so the row always spans
             the card without side-scrolling (same approach as the Users
             table). -->
        <table class="admin-table as-table">
          <thead>
            <tr>
              <th class="as-col-listing">Listing</th>
              <th class="as-col-owner" title="The contact email typed into the post form. Not necessarily an account.">Posted Email</th>
              <th class="as-col-user" title="The account that created the listing. Click to open the full user activity.">Registered User</th>
              <th class="as-col-price">Price</th>
              <th class="as-col-dates">
                <div class="as-duo">
                  <span>Created</span>
                  <span>Modified</span>
                </div>
              </th>
              <th class="as-col-source">Source</th>
              <th class="as-col-act">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.servicesID">
              <!-- Listing: title on top, type + location beneath it. The old
                   Active checkbox column is gone — Enable/Disable in the
                   actions cell is the one control for that, and this badge
                   carries the state the checkbox used to show. -->
              <td class="as-col-listing">
                <div class="as-listing">
                  <span class="as-title" :title="item.servicesName">{{ item.servicesName || '-' }}</span>
                  <span class="as-sub">
                    <span v-if="item.isActive !== 'true'" class="admin-badge admin-badge--danger">Disabled</span>
                    <span v-if="item.serviceType1" class="admin-badge admin-badge--neutral">{{ item.serviceType1 }}</span>
                    <span class="as-loc" :title="item.location">{{ item.location || '-' }}</span>
                  </span>
                </div>
              </td>
              <!-- Plain text, deliberately not a link. A poster can type any
                   address here, so it often matches no account — clicking it
                   used to open the user modal on an email that resolved to
                   nothing and report "user not found". The account lives in
                   the next column, resolved by userID instead. -->
              <td class="as-col-owner">
                <span v-if="item.userEmail" class="as-posted" :title="item.userEmail">{{ item.userEmail }}</span>
                <span v-else class="admin-muted">-</span>
              </td>
              <!-- The registered account behind the listing. This is the one
                   that opens the user-activity modal, and it resolves for
                   every row because it is keyed on the service's userID. -->
              <td class="as-col-user">
                <button
                  v-if="item.accountUserID"
                  type="button"
                  class="owner-link as-acct"
                  :title="`${item.accountEmail || item.accountUserName || item.accountUserID} — view full user details`"
                  @click="openOwner(item)"
                >
                  <span class="as-acct__main">{{ item.accountEmail || item.accountUserName || item.accountUserID }}</span>
                  <span v-if="item.accountUserName && item.accountEmail" class="as-acct__sub">{{ item.accountUserName }}</span>
                </button>
                <span v-else class="admin-muted" title="No registered account is linked to this listing — crawled and imported rows have none.">-</span>
              </td>
              <td class="as-col-price">{{ item.roomPrice || '-' }}</td>
              <!-- Both dates in one cell, aligned under the sub-headers. Short
                   form keeps the column narrow; full timestamp is the title. -->
              <td class="as-col-dates">
                <div class="as-duo as-dates">
                  <span :title="formatDate(item.createdTime)">{{ formatDateShort(item.createdTime) }}</span>
                  <span :title="formatDate(item.updatedTime)">{{ formatDateShort(item.updatedTime) }}</span>
                </div>
              </td>
              <!-- Source: the page this listing was crawled from. An <a> rather
                   than a button so middle-click / copy-link still work; only
                   crawled rows have `url`, posts made on the site don't — those
                   get the "User post" badge instead of an empty dash.
                   A spreadsheet import ("XL Data") gets its own badge and keeps
                   the link beside it: the fb_listings batches recorded the
                   original permalink in `url`, so it is neither a user post
                   with nothing to link nor crawl output. -->
              <td class="as-col-source">
                <span v-if="isUserPost(item)" class="admin-badge admin-badge--neutral">User post</span>
                <template v-else>
                <span v-if="item.listingSource === 'XL Data'" class="admin-badge admin-badge--info as-src-xl">XL Data</span>
                <a
                  v-if="sourceHref(item.url)"
                  class="admin-btn admin-btn--outline admin-btn--sm source-link"
                  :href="sourceHref(item.url)"
                  target="_blank"
                  rel="noopener noreferrer"
                  :title="item.url"
                >
                  <i class="fa-solid fa-up-right-from-square"></i>
                  <span class="source-link__host">{{ sourceHost(item.url) }}</span>
                </a>
                <span v-else-if="item.sourceSite" class="admin-badge admin-badge--neutral">{{ item.sourceSite }}</span>
                <span v-else-if="item.listingSource !== 'XL Data'">-</span>
                </template>
              </td>
              <!-- Shared row-action pattern (.admin-rowbtn) — see admin-theme.css.
                   Every report uses it, so the same action looks the same
                   wherever you meet it. The title is what names the button on a
                   narrow screen, where the label collapses. -->
              <td class="as-col-act">
                <div class="admin-rowactions">
                  <a
                    v-if="postUrl(item)"
                    class="admin-rowbtn"
                    :href="postUrl(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                    :title="`Open the public listing page — ${item.servicesName || item.servicesID}`"
                  ><i class="fa-solid fa-up-right-from-square"></i><span>View listing</span></a>
                  <button
                    type="button"
                    class="admin-rowbtn admin-rowbtn--primary"
                    @click="openEdit(item.servicesID)"
                    title="Edit this posting"
                  ><i class="fa-solid fa-pen"></i><span>Edit</span></button>
                  <button
                    type="button"
                    class="admin-rowbtn"
                    @click="viewAudit(item.servicesID)"
                    title="View audit history"
                  ><i class="fa-solid fa-clock-rotate-left"></i><span>Audit</span></button>
                  <button
                    type="button"
                    class="admin-rowbtn"
                    @click="openRawData(item)"
                    title="View full raw service document"
                  ><i class="fa-solid fa-code"></i><span>Raw</span></button>
                  <button
                    type="button"
                    class="admin-rowbtn"
                    :class="item.isActive === 'true' ? 'admin-rowbtn--danger' : 'admin-rowbtn--success'"
                    @click="toggleActive(item)"
                    :title="item.isActive === 'true' ? 'Hide this posting from the site' : 'Publish this posting again'"
                  ><i class="fa-solid" :class="item.isActive === 'true' ? 'fa-eye-slash' : 'fa-eye'"></i><span>{{ item.isActive === 'true' ? 'Disable' : 'Enable' }}</span></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="items.length" class="admin-pagination">
        <button class="admin-btn admin-btn--outline admin-btn--sm" @click="prevPage" :disabled="currentPage === 1">← Previous</button>
        <span class="admin-pagination__info">Page {{ currentPage }} of {{ totalPages }} ({{ totalCount }} total)</span>
        <button class="admin-btn admin-btn--outline admin-btn--sm" @click="nextPage" :disabled="currentPage >= totalPages">Next →</button>
      </div>

      <div v-else-if="!loading" class="admin-empty">
        <i class="fa-solid fa-inbox"></i>
        <p class="admin-empty__title">No services found</p>
      </div>
    </div>

    <!-- Admin post editor: edit ANY post regardless of creator. Reuses the same
         editor users see, so admins can update every editable field + add photos
         (admins have an unlimited image allowance). -->
    <PostModal
      v-if="showEditModal"
      type="edit"
      :postId="editServiceId"
      @close="onEditClose"
    />

    <!-- Owner activity — full user-activity modal (details + logins + page views
         + posts + messages). Resolved from the listing's userID via the
         Registered User column; the posted email is never used to look a user
         up, because it frequently belongs to no account. -->
    <UserActivityModal
      v-if="showOwnerModal"
      :user-id="ownerRef.userID"
      :email="ownerRef.email"
      @close="showOwnerModal = false"
    />

    <!-- Raw service document modal (full DB row for selected listing). -->
    <teleport to="body">
      <div v-if="showRawModal" class="admin-portal raw-modal-overlay" @click.self="closeRawModal">
        <div class="raw-modal" role="dialog" aria-modal="true" aria-label="Service Raw Data">
          <div class="raw-modal__head">
            <div class="raw-modal__head-left">
              <h3 class="raw-modal__title">Service Raw Data</h3>
              <p class="raw-modal__subtitle" v-if="rawServicesID">servicesID: {{ rawServicesID }}</p>
            </div>
            <div class="raw-modal__actions">
              <button type="button" class="raw-modal__close" @click="closeRawModal" aria-label="Close raw data modal">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>

          <div class="raw-modal__body">
            <div v-if="rawLoading" class="raw-loading-panel">
              <div class="admin-loading"><div class="admin-spinner"></div><span>Loading raw data…</span></div>
            </div>
            <div v-else-if="rawError" class="admin-badge admin-badge--danger">{{ rawError }}</div>
            <div v-else class="raw-fields-wrap">
              <div v-if="!rawFields.length" class="admin-empty">
                <div class="admin-empty__title">No raw fields found</div>
                <p>This listing has no data to display.</p>
              </div>
              <div v-else class="raw-grid">
                <div v-for="field in rawFields" :key="field.key" class="raw-field-card">
                  <div class="raw-field-label">{{ field.label }}</div>
                  <div class="raw-field-value" v-if="!field.isComplex">{{ field.displayValue }}</div>
                  <pre v-else class="raw-field-code">{{ field.complexValue }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import TableSkeleton from "@/components/common/TableSkeleton.vue";
import DatePicker from "@/components/common/DatePicker.vue";
import { ref, onMounted, computed } from 'vue';
import axiosInst from '@/utils/axiosinstance';
import { startOfDayISO, endOfDayISO } from '@/utils/dateRange';
import { useRouter, useRoute } from 'vue-router';
import { toast } from 'vue-sonner';
import PostModal from './Models/PostModal.vue';
import UserActivityModal from './Models/UserActivityModal.vue';
import SearchableSelect from '@/components/pages/Directory/SearchableSelect.vue';
import { postUrl } from '@/utils/postUrl';

const router = useRouter();
const route = useRoute();
const items = ref([]);
const loading = ref(false);
const error = ref('');
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);
const limit = ref(100);
const filterEmail = ref('');
const filterDateFrom = ref('');
const filterDateTo = ref('');
const sortBy = ref('created_desc');
// Provenance: '' = all, 'user' = posted on the site, 'crawled' = crawler-fed.
// Defaults to user posts — crawled rows are ~99.8% of the collection, so an
// unfiltered table is a crawler dump with the handful of real posts lost in it.
const filterSource = ref('user');
// "All" stays a real option rather than relying on the clear button, so the
// current state always reads as words instead of an empty field.
const sourceOptions = [
  { value: 'user', label: 'User posts only' },
  { value: 'crawled', label: 'Crawled only' },
  { value: 'XL Data', label: 'XL Data only' },
  { value: '', label: 'All sources' },
];
const sortOptions = [
  { value: 'created_desc', label: 'Created (newest first)' },
  { value: 'created_asc', label: 'Created (oldest first)' },
  { value: 'updated_desc', label: 'Modified (newest first)' },
  { value: 'updated_asc', label: 'Modified (oldest first)' },
];

// Default lookback for a fresh visit. An unfiltered load aggregates the ENTIRE
// services collection (24k+ rows) just to render page 1, which is what made
// this pane slow to open. The value sits visibly in the From Date input, so
// the admin can widen it — and Clear still empties it for a deliberate
// all-time pull.
const DEFAULT_LOOKBACK_DAYS = 5;
function defaultFromDate() {
  const d = new Date();
  d.setDate(d.getDate() - DEFAULT_LOOKBACK_DAYS);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Email autocomplete (typeahead) state
const emailSuggestions = ref([]);
const showSuggestions = ref(false);
const highlightIndex = ref(-1);
let suggestTimer = null;
let blurTimer = null;

// Admin post editor modal
const showEditModal = ref(false);
const editServiceId = ref(null);

// Owner / user-details modal (clicking the owner email in a row)
const showOwnerModal = ref(false);
const ownerRef = ref({ userID: '', email: '' });

// Raw service-data modal state
const showRawModal = ref(false);
const rawLoading = ref(false);
const rawError = ref('');
const rawServicesID = ref('');
const rawDataObj = ref(null);

const rawFields = computed(() => {
  const obj = rawDataObj.value;
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];

  return Object.keys(obj).map((key) => {
    const value = obj[key];
    const isComplex = value !== null && typeof value === 'object';
    return {
      key,
      label: prettifyFieldName(key),
      isComplex,
      displayValue: isComplex ? '' : formatPrimitiveValue(value),
      complexValue: isComplex ? JSON.stringify(value, null, 2) : '',
    };
  });
});

function prettifyFieldName(name) {
  return (name || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (s) => s.toUpperCase());
}

function formatPrimitiveValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    const d = new Date(value);
    if (!isNaN(d.getTime()) && (/time|date|at/i.test(value) || /^\d{4}-\d{2}-\d{2}T/.test(value))) {
      return d.toLocaleString();
    }
    return value;
  }
  return String(value);
}

function openOwner(item) {
  // userID only. Passing the posted email as a fallback is what surfaced the
  // wrong user (or none) — it belongs to the listing, not to an account.
  ownerRef.value = {
    userID: item.accountUserID || '',
    email: item.accountEmail || '',
  };
  showOwnerModal.value = true;
}

function openEdit(servicesID) {
  editServiceId.value = servicesID;
  showEditModal.value = true;
}

// On save the editor performs a full page reload, so this handler mainly covers
// closing without saving — refresh the list so any change is reflected.
function onEditClose() {
  showEditModal.value = false;
  editServiceId.value = null;
  fetchServices();
}

function goToDashboard() {
  router.push('/');
}

function viewAudit(servicesID) {
  // When opened inside the unified Admin Console, switch the right-pane section
  // instead of leaving for the standalone route — otherwise the console (and its
  // left nav) unmounts. ServiceAudit reads servicesID from the query. Outside the
  // console (the standalone /all-services route) keep the original behaviour.
  // Either way the as* filter keys ride along so Service Audit's "Back to All
  // Services" can land on the same result set the admin was looking at.
  if (route.name === 'admin-console') {
    router.push({ query: { ...route.query, section: 'service-audit', servicesID } });
  } else {
    router.push({ name: 'service-audit-by-id', params: { servicesID }, query: filterQuery() });
  }
}

function closeRawModal() {
  showRawModal.value = false;
  rawLoading.value = false;
  rawError.value = '';
  rawServicesID.value = '';
  rawDataObj.value = null;
}

async function openRawData(item) {
  rawServicesID.value = item?.servicesID || '';
  rawError.value = '';
  rawDataObj.value = null;
  showRawModal.value = true;
  rawLoading.value = true;

  try {
    const { data } = await axiosInst.post('/api/serviceslist', {
      servicesID: item?.servicesID,
      limit: 1,
      page: 1,
    });

    const rawItem = data?.items?.[0] || item;
    rawDataObj.value = rawItem || {};
  } catch (e) {
    rawError.value = 'Failed to fetch full raw data. Showing current row data.';
    rawDataObj.value = item || {};
    toast.error('Failed to fetch full raw data');
  } finally {
    rawLoading.value = false;
  }
}

// ── Filter state in the URL ──────────────────────────────────────────────────
// Without this, drilling into an audit and coming back dropped the admin on an
// unfiltered first page — they had to retype the email or slug. The keys are
// namespaced `as*` because the admin console keeps every pane's params in one
// shared query string.
const FILTER_KEYS = ['asq', 'asfrom', 'asto', 'assort', 'assrc', 'aspage'];

function filterQuery() {
  return Object.fromEntries(
    Object.entries(route.query).filter(([k]) => FILTER_KEYS.includes(k))
  );
}

function seedFromQuery() {
  const q = route.query;
  const str = (v) => (typeof v === 'string' ? v : '');
  filterEmail.value = str(q.asq);
  filterDateFrom.value = str(q.asfrom);
  filterDateTo.value = str(q.asto);
  if (str(q.assort)) sortBy.value = str(q.assort);
  // 'assrc' absent means the default (user posts); 'all' is the explicit
  // no-filter state — it can't ride in the URL as an empty string.
  if ('assrc' in q) filterSource.value = str(q.assrc) === 'all' ? '' : str(q.assrc);
  const p = parseInt(str(q.aspage), 10);
  currentPage.value = Number.isFinite(p) && p > 0 ? p : 1;
  // Fresh visit (no filter state in the URL): default to the recent window.
  // Any as* key present means the admin already chose their view — a restored
  // drill-down return, a shared link, or an intentional cleared/all-time state
  // mid-session — so leave it alone.
  //
  // Skipped while the Source filter is on user posts: that lookback exists only
  // because aggregating all 24k+ rows to render page 1 is slow, and there are
  // ~50 user posts in total — pulling them all time is both fast and what an
  // admin wants to see, where a 5-day window would usually show nothing.
  if (!FILTER_KEYS.some((k) => k in q) && filterSource.value !== 'user') {
    filterDateFrom.value = defaultFromDate();
  }
}

// Switching Source re-runs the query from page 1. Moving off "User posts only"
// re-arms the recent-window default (unless dates are already set), so the
// table doesn't fall back into a full-collection scan of the crawled rows.
function onSourceChange() {
  if (filterSource.value !== 'user' && !filterDateFrom.value && !filterDateTo.value) {
    filterDateFrom.value = defaultFromDate();
  }
  currentPage.value = 1;
  fetchServices();
}

function syncQuery() {
  const next = { ...route.query };
  const set = (k, v) => {
    if (v) next[k] = v;
    else delete next[k];
  };
  set('asq', filterEmail.value.trim());
  set('asfrom', filterDateFrom.value);
  set('asto', filterDateTo.value);
  // Only carry non-defaults, so an untouched page keeps a clean URL.
  set('assort', sortBy.value === 'created_desc' ? '' : sortBy.value);
  set('assrc', filterSource.value === 'user' ? '' : (filterSource.value || 'all'));
  set('aspage', currentPage.value > 1 ? String(currentPage.value) : '');
  // replace(), not push(): filtering shouldn't stack history entries that the
  // browser Back button then has to walk through one at a time.
  router.replace({ query: next }).catch(() => {});
}

function formatDate(date) {
  if (!date) return '-';
  const d = new Date(date);
  return isNaN(d.getTime()) ? (date || '-') : d.toLocaleString();
}

// Table form: "Jul 29, 26" instead of "7/29/2026, 3:42:11 PM". The two date
// columns were the widest thing in the row; formatDate() still supplies the
// full timestamp for the cell's tooltip.
function formatDateShort(date) {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return date || '-';
  return d.toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: 'numeric' });
}

// ── Source page ──────────────────────────────────────────────────────────────
// A post made on the site rather than pulled in by a crawler. Mirrors the
// server-side filter: trust `listingSource` when the row carries it, otherwise
// fall back to the legacy heuristic (crawled rows have a source `url`) so rows
// written before the field existed are still labelled correctly.
function isUserPost(item) {
  if (item.listingSource) return item.listingSource === 'user';
  return !(item.url && String(item.url).trim());
}

// Only render a link for an absolute http(s) URL. Some crawled rows carry a
// bare host or a relative path, and feeding either to href would resolve
// against the admin origin and open a broken /admin/... tab.
function sourceHref(url) {
  if (typeof url !== 'string') return '';
  const u = url.trim();
  return /^https?:\/\/\S+$/i.test(u) ? u : '';
}

// Label the button with the source site rather than the full URL — the URLs
// are long enough to blow the column out. Full URL stays in the tooltip.
function sourceHost(url) {
  try {
    return new URL(url.trim()).hostname.replace(/^www\./, '');
  } catch (e) {
    return 'Source';
  }
}

function clearFilters() {
  filterEmail.value = '';
  filterDateFrom.value = '';
  filterDateTo.value = '';
  sortBy.value = 'created_desc';
  // Clear returns to the page default (user posts), not to an all-sources dump.
  filterSource.value = 'user';
  emailSuggestions.value = [];
  showSuggestions.value = false;
  highlightIndex.value = -1;
  currentPage.value = 1;
  fetchServices();
}

// ── Email typeahead ──────────────────────────────────────────────────────────
function onEmailInput() {
  const q = filterEmail.value.trim();
  highlightIndex.value = -1;
  if (suggestTimer) clearTimeout(suggestTimer);
  // Skip when empty/too short or when the user is pasting a service URL.
  if (q.length < 2 || q.includes('/')) {
    emailSuggestions.value = [];
    showSuggestions.value = false;
    return;
  }
  suggestTimer = setTimeout(fetchEmailSuggestions, 250);
}

async function fetchEmailSuggestions() {
  const q = filterEmail.value.trim();
  if (q.length < 2) return;
  try {
    const { data } = await axiosInst.post('/api/allservices/emailsuggest', { q });
    emailSuggestions.value = data.emails || [];
    showSuggestions.value = emailSuggestions.value.length > 0;
  } catch {
    emailSuggestions.value = [];
    showSuggestions.value = false;
  }
}

function selectEmail(email) {
  filterEmail.value = email;
  showSuggestions.value = false;
  emailSuggestions.value = [];
  highlightIndex.value = -1;
  currentPage.value = 1;
  fetchServices();
}

function moveHighlight(dir) {
  if (!showSuggestions.value || !emailSuggestions.value.length) return;
  const n = emailSuggestions.value.length;
  highlightIndex.value = (highlightIndex.value + dir + n) % n;
}

function onEmailEnter() {
  if (showSuggestions.value && highlightIndex.value >= 0) {
    selectEmail(emailSuggestions.value[highlightIndex.value]);
  } else {
    showSuggestions.value = false;
    currentPage.value = 1;
    fetchServices();
  }
}

function hideSuggestionsSoon() {
  if (blurTimer) clearTimeout(blurTimer);
  blurTimer = setTimeout(() => { showSuggestions.value = false; }, 150);
}

async function fetchServices() {
  error.value = '';
  loading.value = true;
  syncQuery();
  try {
    const payload = {
      limit: limit.value,
      page: currentPage.value,
      sortBy: sortBy.value,
    };

    if (filterEmail.value) {
      payload.filter = filterEmail.value;
    }
    if (filterSource.value) {
      payload.listingSource = filterSource.value;
    }
    // Date-only inputs: widen each day to its full local span so that picking
    // the same day for From and To returns that whole day, not just midnight.
    const dateFrom = startOfDayISO(filterDateFrom.value);
    const dateTo = endOfDayISO(filterDateTo.value);
    if (dateFrom) payload.dateFrom = dateFrom;
    if (dateTo) payload.dateTo = dateTo;

    const { data } = await axiosInst.post('/api/allservices', payload);
    items.value = data.items || [];
    currentPage.value = data.page || 1;
    totalPages.value = data.totalPages || 1;
    totalCount.value = data.totalCount || 0;
  } catch (e) {
    error.value = e?.message || 'Failed to load services';
  } finally {
    loading.value = false;
  }
}

async function toggleActive(item) {
  try {
    const newStatus = item.isActive === 'true' ? 'false' : 'true';
    
    await axiosInst.put('/api/services', {
      servicesID: item.servicesID,
      isActive: newStatus,
    });

    item.isActive = newStatus;
    toast.success(`Service ${newStatus === 'true' ? 'activated' : 'deactivated'}`);
  } catch (e) {
    toast.error('Failed to update service status');
    console.error('Toggle active error:', e);
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    fetchServices();
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchServices();
  }
}

onMounted(() => {
  // Restore whatever filter the URL carries first — this is what makes coming
  // back from Service Audit land on the same rows the admin left.
  seedFromQuery();
  fetchServices();
});
</script>

<style scoped>
/* Page-specific tweaks only — shared look comes from admin-theme.css */
.filters { align-items: flex-end; }
.filters .admin-field { gap: 4px; }
/* The action buttons live in a div, not directly on the <td>: `display: flex`
   on a table cell takes it out of table layout, so the row's bottom divider
   stopped at the last real cell and the buttons escaped the column. */

/* Seven columns fit a laptop viewport at this floor; below it the wrap still
   scrolls (admin-table-wrap--scroll). The theme's 15px/16px cell padding is
   too generous once the row carries this much, so it's tightened here. */
.as-table { min-width: 940px; table-layout: fixed; }
.as-table thead th { padding: 13px 16px; }
.as-table tbody td { padding: 15px 16px; }

/* Widths as shares of the table, summing to 100%, so the row spans the card
   at any width instead of one column swallowing the slack. Same reasoning as
   the Users table — note calc(px + %) is not usable under table-layout:fixed,
   Chromium drops mixed-unit widths and equal-splits the columns instead. */
.as-col-listing { /* the remainder — ~28%. Left unset on purpose: if every
                     column carries a percentage they round up between them and
                     the table overflows its wrap by a pixel or two, which is
                     enough to show a scrollbar. */
  width: auto;
}
.as-col-owner   { width: 15%; }
.as-col-user    { width: 15%; }
.as-col-price   { width: 7%; }
.as-col-dates   { width: 16%; }
.as-col-source  { width: 13%; }
/* Fixed, not a share: the three labels are a constant ~124px wide, so a
   percentage starves them on a narrow card and they spill out of the cell —
   which is what put the horizontal scrollbar back. px and % columns coexist
   fine under table-layout:fixed; it is calc() *mixing* the two in one value
   that Chromium drops. */
.as-col-act     { width: 220px; }

/* Listing: title over "type · location" */
.as-listing { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.as-title { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.as-sub { display: flex; align-items: center; gap: 8px; min-width: 0; font-size: 13px; }
.as-loc { color: var(--g-text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Created / Modified share a cell under aligned sub-headers */
.as-duo { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.as-duo span { white-space: nowrap; }
.as-dates { font-size: 13.5px; color: var(--g-text-secondary); }

/* Row actions use the shared .admin-rowbtn pattern from admin-theme.css. */

.as-col-owner .as-posted,
.as-col-user .owner-link { display: block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* Account email over its user name, same stacked shape the Listing cell uses. */
.as-acct__main { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.as-acct__sub { display: block; font-weight: 400; font-size: 12.5px; color: var(--g-text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.as-col-price { font-variant-numeric: tabular-nums; }

/* Source-page link, styled as a button. text-decoration is reset because
   .admin-btn is written for <button> and inherits the anchor underline. */
.as-col-source { white-space: nowrap; }
/* Tighter than a stock --sm button: the padding and icon gap were eating the
   room the hostname needs, so "eknazar.com" ellipsized at "eknazar.c…". */
.source-link { max-width: 100%; padding: 0 10px; gap: 6px; text-decoration: none; }
.source-link:hover { text-decoration: none; }
/* A few sources have long hostnames (us.classifieds.sulekha.com); ellipsize
   rather than let one row widen the column for everyone. */
.source-link__host { overflow: hidden; text-overflow: ellipsis; }
/* An XL Data row can carry a permalink too, so the badge sits beside the
   source link rather than replacing it — this is the gap between the two. */
.as-src-xl { margin-right: 6px; }

/* Clickable owner email — opens the user-detail modal (mirrors .us-userlink). */
.owner-link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--g-blue);
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}
.owner-link:hover { text-decoration: underline; }

/* Email typeahead dropdown — mirrors the location-suggestion UX. */
.email-field { position: relative; min-width: 340px; }
.email-suggest {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 30;
  margin: 4px 0 0;
  padding: 4px 0;
  list-style: none;
  background: var(--g-surface);
  border: 1px solid var(--g-border);
  border-radius: var(--g-radius);
  box-shadow: var(--g-shadow-2, 0 6px 18px rgba(0, 0, 0, .14));
  max-height: 260px;
  overflow-y: auto;
}
.email-suggest li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13.5px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.email-suggest li i { color: var(--g-text-disabled); font-size: 12.5px; }
.email-suggest li.active { background: var(--g-hover); }

/* Raw data modal */
.raw-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 5000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
}

.raw-modal {
  width: min(1040px, 100vw);
  max-height: calc(100vh - 48px);
  background: #ffffff;
  border: none;
  border-radius: 0;
  box-shadow: var(--g-shadow-2, 0 16px 40px rgba(0, 0, 0, .24));
  display: flex;
  flex-direction: column;
}

.raw-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid var(--g-border);
  background: #ffffff;
}

.raw-modal__title { margin: 0; font-size: 18px; font-weight: 700; }
.raw-modal__subtitle { margin: 4px 0 0; font-size: 12.5px; color: var(--g-text-secondary); }

.raw-modal__head-left { min-width: 0; }
.raw-modal__actions { display: flex; align-items: center; gap: 8px; }

.raw-modal__close {
  width: 30px;
  height: 30px;
  border: 1px solid var(--g-border);
  border-radius: var(--g-radius-sm);
  background: var(--g-surface);
  color: var(--g-text-secondary);
  font-size: 16px;
  cursor: pointer;
}
.raw-modal__close:hover { background: var(--g-hover); }

.raw-modal__body {
  padding: 14px 0 16px;
  overflow: auto;
  background: #ffffff;
}

.raw-loading-panel {
  min-height: 340px;
  padding: 18px 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
}

.raw-fields-wrap {
  padding: 0 16px;
}

.raw-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.raw-field-card {
  border: 1px solid #e5e8ef;
  border-radius: 12px;
  background: #fafbfd;
  padding: 10px 12px;
  min-width: 0;
}

.raw-field-label {
  font-size: 12.5px;
  font-weight: 700;
  color: #51607d;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: .02em;
}

.raw-field-value {
  color: #1f2a44;
  font-size: 14.5px;
  line-height: 1.45;
  word-break: break-word;
}

.raw-field-code {
  margin: 0;
  padding: 8px 10px;
  border-radius: var(--g-radius-sm);
  background: #111827;
  color: #e6edf7;
  font-size: 12.5px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: Consolas, Monaco, 'Courier New', monospace;
}

@media (max-width: 760px) {
  .raw-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
  }

  .raw-modal__head {
    flex-direction: column;
    align-items: stretch;
  }

  .raw-modal__actions {
    justify-content: flex-end;
  }

  .raw-fields-wrap {
    padding: 0 12px;
  }

  .raw-grid {
    grid-template-columns: 1fr;
  }
}
</style>
